import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLicencia } from '../context/LicenciaContext';
import api from '../services/api';
import NotificacionesBell from './NotificacionesBell';
import { initPush } from '../services/pushService';
import logoPrestamio from '../assets/logo_prestamio.png';
import logoPrestamioBlank from '../assets/logo_prestamio_blank.png';
import iconPrestamio from '../assets/icon_prestamio.png';
import iconPrestamioBlank from '../assets/icon_prestamio_blank.png';

const MENU_BASE = [
  { path: '/dashboard',     label: 'Dashboard', icon: '🏠' },
  { path: '/clientes',      label: 'Clientes',  icon: '👥' },
  { path: '/creditos',      label: 'Créditos',  icon: '💰' },
  { path: '/rutas',         label: 'Rutas',     icon: '🗺️' },
  { path: '/cobros',        label: 'Cobros',    icon: '📋' },
  { path: '/reportes',      label: 'Reportes',  icon: '📊' },
  { path: '/usuarios',      label: 'Usuarios',  icon: '⚙️', soloAdmin: true },
  { path: '/configuracion', label: 'Configuración', icon: '💎', soloAdmin: true },
];
const MENU_VENTAS = [
  { path: '/productos', label: 'Inventario', icon: '📦' },
  { path: '/ventas',    label: 'Ventas',     icon: '🛒' },
];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function getInitials(nombre = '') {
  const parts = nombre.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nombre.substring(0, 2).toUpperCase();
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

export default function Layout({ children }) {
  const { usuario, logout, refreshUsuario } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { logoUrl, moduloVentas } = useLicencia();
  const MENU = [...MENU_BASE, ...(moduloVentas ? MENU_VENTAS : [])];
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const fotoRef = useRef(null);

  useEffect(() => { initPush(); }, []);

  function handleLogout() { logout(); navigate('/login'); }

  async function handleFotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('foto', file);
    setUploadingFoto(true);
    try {
      await api.post('/usuarios/me/foto', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshUsuario();
    } catch (_) {} finally {
      setUploadingFoto(false);
      e.target.value = '';
    }
  }

  // Logo en sidebar expandido: wordmark completo
  const wordmark = isDark ? logoPrestamioBlank : logoPrestamio;
  // Logo en sidebar colapsado: solo ícono
  const iconSidebar = isDark ? iconPrestamioBlank : iconPrestamio;
  // Logo de empresa (si tiene): siempre sin filtro
  const companyLogo = logoUrl ? `${API_BASE}${logoUrl}` : null;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-1)', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Sidebar ─────────────────────────────────────────── */}
      <aside style={{
        width: collapsed ? 60 : 220,
        background: 'var(--sb-bg)',
        borderRight: '1px solid var(--sb-border)',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s', minHeight: '100vh',
        position: 'sticky', top: 0, height: '100vh', overflowY: 'auto', flexShrink: 0,
      }}>
        {/* Logo header */}
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0' : '0 14px',
          borderBottom: '1px solid var(--sb-border)',
          height: 52, flexShrink: 0, gap: 8,
        }}>
          {collapsed ? (
            companyLogo
              ? <img src={companyLogo} alt="Logo" style={{ height: 28, width: 28, objectFit: 'contain', borderRadius: 4 }} />
              : <img src={iconSidebar} alt="P" style={{ height: 28, width: 28, objectFit: 'contain' }} />
          ) : (
            <>
              {companyLogo
                ? <img src={companyLogo} alt="Logo" style={{ height: 36, maxWidth: 130, objectFit: 'contain', borderRadius: 6 }} />
                : <img src={wordmark} alt="Prestam.io" style={{ height: 36, maxWidth: 140, objectFit: 'contain' }} />
              }
              <button
                onClick={() => setCollapsed(true)}
                style={{ background: 'none', border: 'none', color: 'var(--sb-text-muted)', cursor: 'pointer', fontSize: '1.1rem', flexShrink: 0, padding: '2px 4px' }}
              >«</button>
            </>
          )}
        </div>

        {/* Expand button cuando está colapsado */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            style={{ background: 'none', border: 'none', color: 'var(--sb-text-muted)', cursor: 'pointer', fontSize: '1rem', padding: '8px 0', borderBottom: '1px solid var(--sb-border)' }}
          >»</button>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, paddingTop: 4 }}>
          {MENU.filter(item => !item.soloAdmin || usuario?.rol === 'admin').map(item => {
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} style={{
                display: 'flex', alignItems: 'center',
                gap: collapsed ? 0 : '0.75rem',
                justifyContent: collapsed ? 'center' : 'flex-start',
                padding: collapsed ? '13px 0' : '11px 14px',
                textDecoration: 'none', fontSize: '0.875rem',
                color: active ? 'var(--sb-nav-active-color)' : 'var(--sb-nav-color)',
                background: active ? 'var(--sb-nav-active-bg)' : 'transparent',
                borderRight: active ? '3px solid var(--sb-nav-active-border)' : '3px solid transparent',
                transition: 'all 0.15s',
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}

          {usuario?.is_superadmin === 1 && (
            <Link to="/superadmin" style={{
              display: 'flex', alignItems: 'center',
              gap: collapsed ? 0 : '0.75rem',
              justifyContent: collapsed ? 'center' : 'flex-start',
              padding: collapsed ? '13px 0' : '11px 14px',
              textDecoration: 'none', color: '#818cf8',
              borderTop: '1px solid var(--sb-border)',
              fontSize: '0.875rem', marginTop: 4,
            }}>
              <span style={{ fontSize: 16 }}>⚡</span>
              {!collapsed && <span>Panel SaaS</span>}
            </Link>
          )}
        </nav>

        {/* Footer: avatar + nombre + logout */}
        <div style={{
          padding: collapsed ? '12px 0' : '12px 14px',
          borderTop: '1px solid var(--sb-border)',
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between', gap: 8,
        }}>
          <input ref={fotoRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleFotoChange} />
          <div
            onClick={() => !uploadingFoto && fotoRef.current?.click()}
            title="Cambiar foto"
            style={{
              width: 34, height: 34, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
              cursor: 'pointer', border: '2px solid var(--sb-border)',
              background: usuario?.foto_url ? 'transparent' : '#6366f1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: uploadingFoto ? 0.5 : 1,
            }}
          >
            {usuario?.foto_url
              ? <img src={`${API_BASE}${usuario.foto_url}`} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>{getInitials(usuario?.nombre)}</span>
            }
          </div>

          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--sb-text)', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                  {usuario?.nombre}
                </div>
                <div style={{ fontSize: 10, color: 'var(--sb-text-muted)', textTransform: 'capitalize' }}>{usuario?.rol}</div>
              </div>
              <button onClick={handleLogout} title="Cerrar sesión" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sb-text-muted)', padding: '4px', borderRadius: 6, flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </>
          )}
        </div>
      </aside>

      {/* ── Right panel ─────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Top bar */}
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2,
          padding: '0 16px', height: 52, flexShrink: 0,
          background: 'var(--tb-bg)',
          borderBottom: '1px solid var(--tb-border)',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <button
            onClick={toggleTheme}
            title={isDark ? 'Modo claro' : 'Modo oscuro'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--tb-icon-color)',
              padding: '7px 8px', borderRadius: 8,
              display: 'flex', alignItems: 'center',
            }}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          <NotificacionesBell />
        </header>

        {/* Content — siempre fondo oscuro */}
        <main style={{ flex: 1, padding: '2rem', overflowY: 'auto', background: 'var(--bg-app)' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
